import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { temaDe } from './temasJuegos';
import { proximaFecha } from './useCarruselJuegos';
import { db } from '../../firebase/firebase.config';
import { MUESTRAS_SORTEO } from './muestrasSorteos';

/**
 * Los extractos por juego — el modelo de vista.
 *
 * Este archivo decide QUÉ se muestra; `ExtractosJuegos.jsx` decide cómo se
 * ve, y `temasJuegos.js` con qué ropa. La separación importa porque las dos
 * fuentes de datos tienen forma distinta y no queremos esa diferencia
 * repartida por el JSX.
 *
 * ── Las dos familias, y por qué no entran en el mismo molde ──────────────
 *
 *  `bolillero`      lotoplus, loto5plus, quini6, brinco
 *                   N modalidades, cada una con SUS números y SU tabla de
 *                   premios. Loto Plus suma el Número PLUS, que no es una
 *                   modalidad más: es una bolilla aparte.
 *                   Fuente: `sorteos_juegos/{juegoClave}-{fechaISO}`.
 *
 *  `pozo-quiniela`  pozo
 *                   Sin modalidades y sin premios: dos sorteos por día, cada
 *                   uno con cinco números de tres cifras, una Super Bola
 *                   aparte, un pozo estimado y una fecha de caducidad.
 *                   Fuente: `extractos/{fecha}.pozo[]`, que ya se lee hoy.
 *
 * Forzar los dos a una grilla de "6 bolillas + tabla" daría una tabla vacía
 * en el Pozo y una fila de guiones donde no hay Super Bola. La familia decide
 * la estructura; el juego pone el vestido.
 *
 * ── N1 ──────────────────────────────────────────────────────────────────
 * Ninguna rama de este archivo produce un número, un monto o una fecha que no
 * haya salido de la fuente. Lo que falta no se rellena: se omite, y la vista
 * simplemente no dibuja esa pieza. `VACANTE` no es un cero y un `null` no es
 * un cero: los tres viajan distinguidos hasta la pantalla.
 */

/**
 * Los juegos cuyo extracto se lee de `sorteos_juegos`.
 *
 * Encendida el 21/08/2026. El 20/08 los dos documentos daban 403; hoy las
 * reglas los declaran con lectura pública (ruleset 111bbe3b, desplegado el
 * 21/08 03:25 UTC), la ingesta está desplegada y `recuperarSorteos` ya escribió
 * `sorteos_juegos/lotoplus-2026-08-19` y `…/loto5plus-2026-08-15`.
 *
 * Van SOLO los dos juegos que el backend ingesta. Quini 6 y Brinco tienen su
 * vista construida y probada con datos de la fuente oficial, pero no tienen
 * ingesta: `functions/sorteos/parser.js` no los declara. Agregarlos acá dibuja
 * un panel que no puede traer nada.
 *
 * El orden de esta lista es el orden en que se dibujan.
 */
export const JUEGOS_CON_EXTRACTO = ['lotoplus', 'loto5plus'];

/** Nombre y familia de cada juego. Un juego sin entrada igual se dibuja. */
const CATALOGO = {
  lotoplus: { nombre: 'Loto Plus', familia: 'bolillero' },
  quini6: { nombre: 'Quini 6', familia: 'bolillero' },
  brinco: { nombre: 'Brinco', familia: 'bolillero' },
  loto5plus: { nombre: 'Loto 5 Plus', familia: 'bolillero' },
  pozo: { nombre: 'Pozo de la Quiniela', familia: 'pozo-quiniela' },
};

const PUB = process.env.PUBLIC_URL || '';

/* ────────────────────────── modo muestra ────────────────────────────── */

/**
 * La única referencia a las muestras en todo el archivo, y a propósito.
 *
 * En el bundle de producción esto es `'production' === 'production' ? null :
 * …`, o sea la constante `null`, y `MUESTRAS_SORTEO` se queda sin un solo
 * consumidor: webpack marca el export como no usado y el minificador borra el
 * objeto entero. **Verificado sobre el build real** (`grep MITJANS
 * build/static/js/*.js` no encuentra nada): las muestras no viajan.
 *
 * La primera versión repartía la referencia por el archivo y el JSON quedaba
 * en el bundle. Que no se pudiera renderizar no alcanzaba: 19 KB de extractos
 * dentro del bundle de un organismo público de juego es un dato publicado,
 * aunque ninguna pantalla lo dibuje.
 */
const MUESTRAS = process.env.NODE_ENV === 'production' ? null : MUESTRAS_SORTEO;

/**
 * ¿Está encendido el modo muestra? Se pide con `?muestra=1`, en la query o
 * después del `#` (la app usa HashRouter), y SOLO responde que sí fuera de
 * producción. Es el segundo de los tres candados; el tercero es la banda
 * ámbar que la vista dibuja cuando esto devuelve `true`.
 */
export function modoMuestra() {
  if (!MUESTRAS) return false;
  if (typeof window === 'undefined' || !window.location) return false;
  const h = String(window.location.hash || '');
  const i = h.indexOf('?');
  const busca = [
    window.location.search || '',
    i === -1 ? '' : `?${h.slice(i + 1)}`,
  ].join('&');
  try {
    return new URLSearchParams(busca.replace(/^&/, '')).get('muestra') === '1';
  } catch (e) {
    return false;
  }
}

/* ─────────────────────────── formatos ────────────────────────────────── */

/**
 * Un monto en pesos, con los centavos SOLO si los tiene.
 *
 * No se redondea a millones ni se abrevia: $3.455.255.110,50 redondeado a
 * $3.455 millones es otro número, y el sitio de un organismo público de juego
 * publica el que dice la fuente. La abreviatura la puede hacer el ojo; el
 * dato no se toca.
 */
export function pesos(valor) {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) return null;
  const centavos = Math.abs(valor % 1) > 0.0001;
  return valor.toLocaleString('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: centavos ? 2 : 0,
    maximumFractionDigits: centavos ? 2 : 0,
  });
}

/** Cantidad de personas, con separador de miles. */
export function cantidad(valor) {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) return null;
  return valor.toLocaleString('es-AR');
}

/** YYYY-MM-DD → DD/MM/YYYY. Devuelve null si no es una fecha ISO. */
export function comoDDMM(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

/**
 * Rótulo de una categoría de premio.
 *
 * Santa Fe rotula sus filas y ese texto viaja en `etiqueta`: se usa TAL CUAL.
 * El XML de Lotería de la Ciudad no rotula nada y trae la clave en el sufijo
 * del tag (`<Premio01>` → "01"). El "01/02/03" se escribe como el ordinal que
 * la fuente hermana ya usa para la misma posición ("1° Premio", "2° Premio",
 * "3° Premio"), que es la única lectura con respaldo.
 *
 * Cualquier otra clave se muestra **tal cual viene**. Es a propósito: el XML
 * del Número PLUS trae `6T`, `5T`, `4T`, `6M`, `5M`, `4M`, `6D`, `SS` y la
 * fuente no declara a qué corresponde cada sigla. Adivinar que "6T" es "6
 * aciertos de Tradicional" es plausible y no está escrito en ningún lado:
 * sería inventar un rótulo sobre un dato real, que es la mitad de N1 que se
 * olvida.
 */
export function rotuloCategoria(cat) {
  if (cat.etiqueta) return cat.etiqueta;
  const m = /^0?(\d)$/.exec(String(cat.clave || ''));
  if (m) return `${m[1]}° premio`;
  return String(cat.clave || '');
}

/* ───────────────────── normalización de un sorteo ─────────────────────── */

/**
 * Qué columnas tiene la tabla de premios de una modalidad.
 *
 * Se decide MIRANDO LOS DATOS, nunca por juego ni por posición: la spec
 * verificó cinco layouts distintos entre dos juegos —Brinco `Extracciones` no
 * tiene columna de pozo, Quini 6 `Siempre Sale` sí tiene aciertos, `Premio
 * Extra` no tiene ni siquiera la columna de premio—. Una columna sin un solo
 * valor no se dibuja: una tabla llena de guiones no informa, ocupa.
 */
function columnasDe(categorias) {
  const hay = (campo) => categorias.some((c) => c[campo] !== null && c[campo] !== undefined);
  return {
    rotulo: categorias.some((c) => c.etiqueta || /^0?\d$/.test(String(c.clave || ''))),
    pozo: hay('pozo'),
    aciertos: hay('aciertos'),
    ganadores: categorias.some((c) => c.vacante || c.ganadores !== null),
    premioUnitario: hay('premioUnitario'),
  };
}

/**
 * Marca la 2ª aparición en adelante de cada número repetido.
 *
 * El `Premio Extra` de Quini 6 es la unión de los números de las cuatro
 * modalidades, con repetidos. La app de Santa Fe los tacha —clase
 * `cuadradoPET`, `text-decoration:line-through`—. El backend guarda la lista
 * plana, así que la marca se DERIVA acá. No es una interpretación: sobre una
 * lista ordenada, "segunda aparición en adelante" da exactamente el mismo
 * conjunto que tacha la fuente (verificado contra el concurso 3401).
 */
function repetidosDe(numeros) {
  const vistos = new Set();
  return numeros.map((n) => {
    if (vistos.has(n)) return true;
    vistos.add(n);
    return false;
  });
}

/** Un `modalidades[]` del backend → un bloque de la vista. */
function bloqueDeModalidad(m) {
  const numeros = Array.isArray(m.numeros) ? m.numeros : [];
  const categorias = Array.isArray(m.categorias) ? m.categorias : [];
  // Una categoría sin un solo dato es ruido: existe en el XML porque el
  // elemento está, vacío. `<Premio5T/>` no es "cero premios", es "esta
  // categoría no aplica" — y lo que no aplica no se dibuja.
  const utiles = categorias.filter(
    (c) => c.vacante || c.pozo !== null || c.ganadores !== null || c.premioUnitario !== null
  );
  const notas = [...new Set(utiles.map((c) => c.nota).filter(Boolean))];

  return {
    clave: m.clave,
    nombre: m.nombre || m.clave,
    numeros,
    repetidos: repetidosDe(numeros),
    categorias: utiles,
    columnas: columnasDe(utiles),
    aciertos: typeof m.aciertos === 'number' ? m.aciertos : null,
    desconocida: !!m.desconocida,
    notas,
    destacado: null,
    meta: [],
  };
}

/**
 * `sorteos_juegos/{id}` → el modelo de vista de un juego de bolillero.
 *
 * El Número PLUS sale del arreglo de modalidades y sube a nivel de juego: no
 * es una modalidad más, es una bolilla que se juega aparte y multiplica el
 * premio de las otras. El sitio oficial de Loto Plus lo dibuja igual, en un
 * bloque propio debajo de las cuatro tarjetas.
 */
function juegoDeSorteo(clave, d, muestra) {
  if (!d) return null;
  const meta = CATALOGO[clave] || {};
  const modalidades = Array.isArray(d.modalidades) ? d.modalidades : [];

  const mPlus = modalidades.find((m) => m.clave === 'plus');
  const bloques = modalidades
    .filter((m) => m.clave !== 'plus')
    .filter((m) => Array.isArray(m.numeros) && m.numeros.length)
    .map(bloqueDeModalidad);

  /* La nota del reglamento que explica la acumulación viene repetida en cada
     modalidad vacante: en el concurso 3401 de Quini 6 son tres copias de las
     mismas tres líneas, y juntas pesan más que los números, que son lo que se
     vino a ver. La que aparece en más de un bloque sube al pie del panel y se
     dice una sola vez. La que aparece en uno solo se queda donde está, porque
     ahí sí es una aclaración de esa modalidad. */
  const cuenta = new Map();
  bloques.forEach((b) => b.notas.forEach((n) => cuenta.set(n, (cuenta.get(n) || 0) + 1)));
  const notasComunes = [...cuenta.entries()].filter(([, c]) => c > 1).map(([n]) => n);
  bloques.forEach((b) => { b.notas = b.notas.filter((n) => !notasComunes.includes(n)); });

  return {
    notasComunes,
    clave,
    nombre: d.juegoNombre || meta.nombre || clave,
    familia: meta.familia || d.familia || 'bolillero',
    entidad: d.entidad || null,
    fuente: d.fuente || null,
    sorteoNro: typeof d.sorteoNro === 'number' ? d.sorteoNro : null,
    fecha: d.fecha || comoDDMM(d.fechaISO),
    fechaISO: d.fechaISO || null,
    hora: d.hora || null,
    // Se mira `pozoEstimadoDudoso` ANTES que `pozoEstimado`: el campo puede
    // tener un valor y ser el de la corrida anterior, conservado a propósito.
    pozoEstimado: d.pozoEstimadoDudoso ? null : (
      typeof d.pozoEstimado === 'number' ? d.pozoEstimado : null),
    pozoDudoso: !!d.pozoEstimadoDudoso,
    proximo: proximaFecha(d.proximoSorteoISO),
    proximoHora: d.proximoSorteoHora || null,
    prescripcion: comoDDMM(d.prescripcionISO),
    autoridades: Array.isArray(d.autoridades) ? d.autoridades.filter((a) => a && a.nombre) : [],
    extractoPdfUrl: d.extractoPdfUrl || null,
    extractoUrl: d.extractoUrl || null,
    soloExtractoOficial: !!d.soloExtractoOficial,
    bloques,
    plus: mPlus && mPlus.numeros && mPlus.numeros.length
      ? { nombre: mPlus.nombre || 'Número PLUS', numero: mPlus.numeros[0] }
      : null,
    tema: temaDe(clave, PUB),
    muestra: !!muestra,
  };
}

/**
 * `extractos/{fecha}.pozo[]` → el modelo de vista del Pozo de la Quiniela.
 *
 * Un panel con un bloque por sorteo del día. Los dos sorteos comparten logo y
 * marca, así que repetir la cabecera sería repetir la misma marca dos veces
 * en la misma pantalla — el mismo criterio que ya sacó los logos de las 25
 * celdas del tablero de la quiniela.
 *
 * Acá no hay premios ni ganadores porque la fuente no los publica. La tabla
 * directamente no existe, en vez de existir vacía.
 */
function juegoDePozo(extractos) {
  const lista = Array.isArray(extractos?.pozo) ? extractos.pozo : [];
  const conNumeros = lista.filter((p) => p && Array.isArray(p.numeros) && p.numeros.length);
  if (!conNumeros.length) return null;

  const bloques = conNumeros.map((p, i) => ({
    clave: p.id || `pozo-${i}`,
    nombre: p.sorteoNro ? `Sorteo Nº ${p.sorteoNro}` : `Sorteo ${i + 1}`,
    numeros: p.numeros,
    repetidos: repetidosDe(p.numeros),
    categorias: [],
    columnas: columnasDe([]),
    aciertos: null,
    desconocida: false,
    notas: [],
    // La Super Bola no es una bolilla más de la fila: la fuente la pinta de
    // otro color y se juega aparte. Va como pieza destacada del bloque.
    destacado: p.superBola !== null && p.superBola !== undefined && p.superBola !== ''
      ? { valor: String(p.superBola), rotulo: 'Super Bola' }
      : null,
    meta: [
      // `pozoEstimado` llega como string sin normalizar ("40.785.000"). Se
      // muestra tal cual vino: convertirlo a número exige adivinar la
      // convención de separadores, y equivocarse ahí multiplica el pozo por
      // cien. Es la misma decisión que ya tomó el carrusel del hero.
      p.pozoEstimado ? { rotulo: 'Pozo estimado', valor: `$${p.pozoEstimado}` } : null,
      p.caduca ? { rotulo: 'Caduca', valor: p.caduca } : null,
    ].filter(Boolean),
  }));

  return {
    notasComunes: [],
    clave: 'pozo',
    nombre: CATALOGO.pozo.nombre,
    familia: 'pozo-quiniela',
    entidad: null,
    fuente: extractos?.fuente || null,
    sorteoNro: null,
    fecha: extractos?.fecha || null,
    fechaISO: extractos?.fechaISO || null,
    hora: null,
    pozoEstimado: null,
    pozoDudoso: false,
    proximo: null,
    proximoHora: null,
    prescripcion: null,
    autoridades: [],
    extractoPdfUrl: null,
    extractoUrl: null,
    soloExtractoOficial: false,
    bloques,
    plus: null,
    tema: temaDe('pozo', PUB),
    muestra: false,
  };
}

/**
 * Devuelve `{ juegos, cargando, muestra }`.
 *
 * `extractos` entra por parámetro y no con un `useExtractos()` propio: la home
 * ya los pidió para el tablero y para la marquesina, y volver a llamar al hook
 * duplicaría dos lecturas de Firestore en cada visita.
 */
export function useExtractosJuegos(extractos, cargandoExtractos) {
  const muestra = useMemo(modoMuestra, []);
  const claves = muestra ? Object.keys(MUESTRAS) : JUEGOS_CON_EXTRACTO;

  const [sorteos, setSorteos] = useState(() => (muestra ? MUESTRAS : null));
  const [cargandoSorteos, setCargandoSorteos] = useState(
    !muestra && JUEGOS_CON_EXTRACTO.length > 0
  );

  useEffect(() => {
    if (muestra || !JUEGOS_CON_EXTRACTO.length) return undefined;
    let vivo = true;

    (async () => {
      /* Dos vueltas, no una: `sorteos_estado/{juego}` dice CUÁL es el último
         sorteo (`ultimoSorteoId`) y `sorteos_juegos/{id}` tiene el detalle
         completo. La colección de detalle está indexada por juego+fecha, así
         que sin el puntero habría que adivinar la fecha del último sorteo —y
         adivinarla mal es pedir un documento que no existe cada vez que la
         entidad corre un sorteo fuera de calendario.

         Cada vuelta va en PARALELO y con `allSettled`: que un juego falte o
         falle no puede dejar a los otros tres en el esqueleto. */
      const punteros = await Promise.allSettled(
        JUEGOS_CON_EXTRACTO.map((c) => getDoc(doc(db, `sorteos_estado/${c}`)))
      );
      if (!vivo) return;

      const pedidos = [];
      punteros.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.warn('No se pudo leer sorteos_estado:', r.reason?.message || r.reason);
          return;
        }
        const id = r.value.exists() ? r.value.data().ultimoSorteoId : null;
        if (id) pedidos.push({ clave: JUEGOS_CON_EXTRACTO[i], id });
      });

      const detalles = await Promise.allSettled(
        pedidos.map((p) => getDoc(doc(db, `sorteos_juegos/${p.id}`)))
      );
      if (!vivo) return;

      const encontrados = {};
      detalles.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value.exists()) {
          encontrados[pedidos[i].clave] = r.value.data();
        } else if (r.status === 'rejected') {
          console.warn('No se pudo leer sorteos_juegos:', r.reason?.message || r.reason);
        }
      });

      setSorteos(encontrados);
      setCargandoSorteos(false);
    })();

    return () => { vivo = false; };
  }, [muestra]);

  const juegos = useMemo(() => {
    const salida = [];
    claves.forEach((c) => {
      const j = juegoDeSorteo(c, sorteos?.[c], muestra);
      // Un juego sin ninguna modalidad con números no entra: antes que un
      // panel hueco, un panel menos.
      if (j && j.bloques.length) salida.push(j);
    });
    const p = juegoDePozo(extractos);
    if (p) salida.push(p);
    return salida;
    // `claves` se deriva de constantes; no hace falta en las dependencias.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorteos, extractos, muestra]);

  return {
    juegos,
    cargando: cargandoExtractos || cargandoSorteos,
    muestra,
  };
}
